import { createContext, useContext, useEffect, useState } from 'react';
import { useAuthStore } from '@/lib/api-functions/api-auth';
import { useGlossaryBlueprintsData } from "@/lib/useTemplatesData";
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';

// Create context with the same interface as useGlossaryBlueprintsData
const GlossaryBlueprintsDataContext = createContext<ReturnType<typeof useGlossaryBlueprintsData> | null>(null);

// Separate component that only renders when authenticated and has a selected game
const AuthenticatedGlossaryBlueprintsDataProvider = ({ children }: { children: React.ReactNode }) => {
  const { selectedGame } = useSimplifiedUserData();
  
  const glossaryBlueprintsData = useGlossaryBlueprintsData({
    gameId: selectedGame?.id,
    autoLoad: true, // Enable auto-loading
  });

  // Debug logging
  useEffect(() => {
  }, [selectedGame?.id, glossaryBlueprintsData.glossaryBlueprints.length]);

  return (
    <GlossaryBlueprintsDataContext.Provider value={glossaryBlueprintsData}>
      {children}
    </GlossaryBlueprintsDataContext.Provider>
  );
};

export const GlossaryBlueprintsDataProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <GlossaryBlueprintsDataContext.Provider value={null}>
        {children}
      </GlossaryBlueprintsDataContext.Provider>
    );
  }

  // If authenticated, render the authenticated provider
  if (isAuthenticated && user?.id) {
    return <AuthenticatedGlossaryBlueprintsDataProvider>{children}</AuthenticatedGlossaryBlueprintsDataProvider>;
  }

  // If not authenticated, provide null context
  return (
    <GlossaryBlueprintsDataContext.Provider value={null}>
      {children}
    </GlossaryBlueprintsDataContext.Provider>
  );
};

export const useGlossaryBlueprintsDataContext = () => {
  const context = useContext(GlossaryBlueprintsDataContext);
  if (!context) {
    throw new Error('useGlossaryBlueprintsDataContext must be used within GlossaryBlueprintsDataProvider and user must be authenticated');
  }
  return context;
};

// Optional hook that returns null if not in context (for use in components that may be outside the provider)
export const useGlossaryBlueprintsDataContextOptional = () => {
  return useContext(GlossaryBlueprintsDataContext);
}; 
